import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, DollarSign, ShoppingBag, TrendingUp, Plus, Edit, Trash2, Eye, 
  LogOut, Settings, Home, AlertTriangle, BarChart3, RefreshCw, Zap, Target,
  ChevronDown, Star, MessageCircle, Clock, CheckCircle2, Ban, Loader, Calendar, Phone, Mail, MapPin, Award
} from 'lucide-react';
import { productsAPI, ordersAPI, authAPI } from '../services/api';

export default function VendorDashboard() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  
  const [user, setUser] = useState(authAPI.getCurrentUser()); 
  const profileMenuRef = useRef(null);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/80x80?text=Pas+d%27image';
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = 'http://localhost:8000';
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${baseUrl}${cleanPath}`;
  };
  
  const getUserAvatar = () => {
    if (user?.profile_photo) return getImageUrl(user.profile_photo);
    if (user?.profile_photo_url) return user.profile_photo_url;
    return null;
  };

  const loadData = useCallback(async (isRefresh = false) =>{
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [productsData, ordersData] = await Promise.all([
        productsAPI.getVendorProducts(),
        ordersAPI.getVendorOrders()
      ]);

      setProducts(Array.isArray(productsData?.results) ? productsData.results : Array.isArray(productsData) ? productsData : []);
      setOrders(Array.isArray(ordersData?.results) ? ordersData.results : Array.isArray(ordersData) ? ordersData : []);
      
    } catch (error) {
      console.error('Erreur chargement données:', error);
      setProducts([]);
      setOrders([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []); // Correction boucle infinie : loadData est stable.

  // 1. Logique de vérification d'utilisateur et chargement initial des données
  useEffect(() => {
    if (!user || user.user_type !== 'vendeur') {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, navigate, loadData]);


  // 2. Logique de gestion de l'ouverture/fermeture du menu du profil (impliquée par l'erreur de syntaxe)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    
    if (isProfileMenuOpen) {
        document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]); // FIX DE LA SYNTAXE : la parenthèse fermante est ajoutée.
  const handleRefresh = () => loadData(true);
  const handleLogout = () => {
    authAPI.logout();
    navigate('/login');
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        await productsAPI.delete(productId);
        setProducts(products.filter(p => p.id !== productId));
        alert('Produit supprimé avec succès');
      } catch (error) {
        console.error('Erreur suppression:', error);
        alert('Erreur lors de la suppression du produit');
      }
    }
  };
  
  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        icon: Clock,
        label: 'En attente',
        color: 'yellow',
        gradient: 'from-yellow-500 to-orange-500',
        bgClass: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
        description: 'En attente de votre validation'
      },
      processing: {
        icon: Loader,
        label: 'En cours',
        color: 'blue',
        gradient: 'from-blue-500 to-cyan-500',
        bgClass: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
        description: 'Préparation de la commande en cours'
      },
      shipped: {
        icon: Package, // Truck pour le buyer, mais Package ici pour le produit ou Truck si on veut
        label: 'Expédié',
        color: 'purple',
        gradient: 'from-purple-500 to-pink-500',
        bgClass: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
        description: 'Colis en cours de livraison'
      },
      delivered: {
        icon: CheckCircle2,
        label: 'Livré',
        color: 'green',
        gradient: 'from-green-500 to-emerald-500',
        bgClass: 'bg-green-500/20 text-green-400 border-green-500/50',
        description: 'Commande finalisée et livrée'
      },
      cancelled: {
        icon: Ban,
        label: 'Annulé',
        color: 'red',
        gradient: 'from-red-500 to-pink-500',
        bgClass: 'bg-red-500/20 text-red-400 border-red-500/50',
        description: 'Commande annulée'
      }
    };
    return configs[status] || configs.pending;
  };
  
  const userAvatarUrl = getUserAvatar();
  
  const AvatarFallback = ({ username, size = '10' }) => (
    <div className={`relative w-${size} h-${size} bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg`}>
      {username?.charAt(0).toUpperCase()}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-orange-500/30 border-t-orange-500 mx-auto mb-6"></div>
            <div className="absolute inset-0 rounded-full h-20 w-20 border-4 border-orange-500/10 blur-xl mx-auto animate-pulse"></div>
          </div>
          <div className="text-xl text-gray-300 font-medium animate-pulse">Chargement de votre espace...</div>
        </div>
      </div>
    );
  }

  const stats = {
    totalProducts: products.length,
    activeProducts: products.filter(p => Number(p.stock) > 0).length,
    totalSales: orders.reduce((sum, o) => sum + (o.items_count || 0), 0),
    revenue: orders.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0),
    lowStock: products.filter(p => {
      const stock = Number(p.stock) || 0;
      return stock > 0 && stock < 10;
    }).length,
    outOfStock: products.filter(p => Number(p.stock) === 0).length,
    pendingOrders: orders.filter(o => o.status === 'pending').length,
    completedOrders: orders.filter(o => o.status === 'delivered').length,
    totalStock: products.reduce((sum, p) => sum + (Number(p.stock) || 0), 0),
  };

  const averageOrderValue = orders.length > 0 ? stats.revenue / orders.length : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-20 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-gradient-to-tl from-orange-500/15 to-purple-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
      </div>

      {/* Header - Harmonisé avec BuyerDashboard */}
      <header className="bg-gray-900/90 backdrop-blur-lg shadow-2xl sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div 
                className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent cursor-pointer hover:scale-105 transition-transform"
                onClick={() => navigate('/')}
              >
                MonMarket
              </div>
              <span className="hidden sm:block text-white/20">|</span>
              <div className="hidden sm:flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-orange-500/20 to-pink-500/20 rounded-xl border border-orange-500/30">
                <BarChart3 size={18} className="text-orange-400" />
                <span className="text-white/90 font-medium text-sm">Espace Vendeur</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button 
                onClick={handleRefresh}
                disabled={refreshing}
                className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all disabled:opacity-50 relative overflow-hidden"
                title="Actualiser"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <RefreshCw size={20} className={`relative z-10 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              
              <button 
                onClick={() => navigate('/')} 
                className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
                title="Accueil"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <Home size={20} className="relative z-10" />
              </button>
              
              {/* Dropdown Profile Menu - Copié/Adapté de BuyerDashboard */}
              <div className="relative" ref={profileMenuRef}>
                <button 
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center space-x-2 ml-4 pl-4 py-2 pr-4 border-l border-white/10 group bg-white/5 rounded-2xl hover:bg-white/10 transition-all"
                >
                  <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full blur opacity-75 group-hover:opacity-100 transition"></div>
                    {userAvatarUrl ? (
                        <img 
                            src={userAvatarUrl}
                            alt="Avatar"
                            className="relative w-10 h-10 rounded-full object-cover border-2 border-white/50 shadow-lg"
                            onError={(e) => e.target.outerHTML = `<div class="relative w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg">${user?.username?.charAt(0).toUpperCase()}</div>`}
                        />
                    ) : (
                        <AvatarFallback username={user?.username} />
                    )}
                  </div>
                  <div className="text-left hidden sm:block">
                    <div className="text-sm font-semibold text-white">{user?.username}</div>
                    <div className="text-xs text-orange-400/80">BIENVENU(E)</div>
                  </div>
                  <ChevronDown size={16} className={`text-white transition-transform ${isProfileMenuOpen ? 'rotate-180' : 'rotate-0'}`} />
                </button>
                
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-3 w-72 sm:w-80 p-4 origin-top-right bg-gray-800 backdrop-blur-lg rounded-2xl shadow-3xl ring-1 ring-white/20 transition-all animate-in zoom-in-95 duration-300">
                    <div className="text-center mb-6 border-b border-gray-700 pb-4">
                        <div className="relative inline-block mb-4">
                            <div className="absolute -inset-2 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full blur animate-pulse"></div>
                            {userAvatarUrl ? (
                                <img 
                                    src={userAvatarUrl}
                                    alt="Avatar"
                                    className="relative w-24 h-24 rounded-full object-cover border-4 border-white shadow-2xl"
                                    onError={(e) => e.target.outerHTML = `<div class="relative w-24 h-24 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white text-3xl font-black shadow-2xl">${user?.username?.charAt(0).toUpperCase()}</div>`}
                                />
                            ) : (
                                <div className="relative w-24 h-24 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white text-3xl font-black shadow-2xl">
                                    {user?.username?.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <h3 className="text-xl font-black text-white mb-1">{user?.username}</h3>
                        <div className="inline-flex items-center space-x-2 bg-orange-500/20 px-3 py-1 rounded-full border border-orange-500/30">
                            <Award size={14} className="text-orange-400" />
                            <span className="text-xs text-orange-300 font-medium">Vendeur depuis {new Date(user?.date_joined || Date.now()).getFullYear()}</span>
                        </div>
                    </div>

                    <div className="space-y-3 mb-6 border-b border-gray-700 pb-4">
                        <div className="group/item flex items-center space-x-3 text-gray-300 bg-gray-700/50 rounded-xl p-3 hover:bg-gray-700 transition-all border border-gray-700 hover:border-orange-500/30">
                            <div className="w-8 h-8 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                                <Mail size={16} className="text-orange-400" />
                            </div>
                            <span className="text-sm truncate font-medium">{user?.email}</span>
                        </div>
                        
                        {user?.phone && (
                            <div className="group/item flex items-center space-x-3 text-gray-300 bg-gray-700/50 rounded-xl p-3 hover:bg-gray-700 transition-all border border-gray-700 hover:border-orange-500/30">
                                <div className="w-8 h-8 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                                    <Phone size={16} className="text-orange-400" />
                                </div>
                                <span className="text-sm font-medium">{user.phone}</span>
                            </div>
                        )}
                        
                        {user?.address && (
                            <div className="group/item flex items-start space-x-3 text-gray-300 bg-gray-700/50 rounded-xl p-3 hover:bg-gray-700 transition-all border border-gray-700 hover:border-orange-500/30">
                                <div className="w-8 h-8 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <MapPin size={16} className="text-orange-400" />
                                </div>
                                <span className="text-sm font-medium">{user.address}</span>
                            </div>
                        )}
                    </div>
                    
                    <button 
                      onClick={() => { navigate('/profile/edit'); setIsProfileMenuOpen(false); }}
                      className="group/btn relative w-full overflow-hidden mb-3"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover/btn:opacity-100 transition-opacity rounded-xl"></div>
                      <div className="relative bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-bold shadow-2xl transform group-hover/btn:scale-[1.02] transition-all flex items-center justify-center space-x-2 text-sm">
                        <Settings size={18} />
                        <span>Modifier le profil</span>
                      </div>
                    </button>
                    
                    <button 
                      onClick={handleLogout} 
                      className="group/btn relative w-full overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-red-600 opacity-0 group-hover/btn:opacity-100 transition-opacity rounded-xl"></div>
                      <div className="relative bg-red-500/20 text-red-400 py-3 rounded-xl font-bold shadow-2xl transform group-hover/btn:scale-[1.02] transition-all flex items-center justify-center space-x-2 text-sm border border-red-500/30">
                        <LogOut size={18} />
                        <span>Déconnexion</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
              
              {/* Logout button (visible only on small screens or as a secondary option) */}
              <button 
                onClick={handleLogout} 
                className="group p-2.5 text-red-400/80 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all ml-2 relative overflow-hidden hidden" // Cache par défaut
                title="Déconnexion"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/0 to-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <LogOut size={20} className="relative z-10" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10">
        <h1 className="text-3xl sm:text-4xl font-black text-white mb-8 flex items-center space-x-3">
            <BarChart3 className="text-orange-400" size={36} />
            <span>Votre Tableau de Bord Vendeur</span>
        </h1>
        
        {/* Stats Cards - Animation au hover */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {[
            {
              icon: Package,
              color: 'blue',
              gradient: 'from-blue-500 to-cyan-500',
              label: 'Produits actifs',
              value: stats.activeProducts,
              subtitle: `sur ${stats.totalProducts} total`,
              detail: `${stats.totalStock} unités`,
              detailLabel: 'Stock Total',
              action: () => {}
            },
            {
              icon: ShoppingBag,
              color: 'green',
              gradient: 'from-green-500 to-emerald-500',
              label: 'Articles vendus',
              value: stats.totalSales,
              subtitle: `${stats.completedOrders} livrées`,
              detail: `${orders.length} total`,
              detailLabel: 'Commandes',
              action: () => navigate('/orders')
            },
            {
              icon: DollarSign,
              color: 'yellow',
              gradient: 'from-yellow-500 to-orange-500',
              label: 'Revenus FCFA',
              value: `${(stats.revenue / 1000).toFixed(0)}k`,
              subtitle: `${stats.revenue.toLocaleString()} CFA`,
              detail: `${averageOrderValue.toFixed(0).toLocaleString()} CFA`,
              detailLabel: 'Panier moyen',
              action: () => {}
            },
            {
              icon: AlertTriangle,
              color: 'red',
              gradient: 'from-red-500 to-pink-500',
              label: 'Stock faible',
              value: stats.lowStock,
              subtitle: '< 10 unités',
              detail: `${stats.outOfStock} produits`,
              detailLabel: 'Ruptures',
              action: () => {}
            }
          ].map((stat, idx) => (
            <div 
              key={idx}
              onMouseEnter={() => setHoveredCard(idx)}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={stat.action}
              className="group relative cursor-pointer"
            >
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.gradient} rounded-2xl blur opacity-0 group-hover:opacity-30 transition duration-500`}></div>
              <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-14 h-14 bg-gradient-to-br ${stat.gradient} rounded-2xl flex items-center justify-center shadow-lg transform group-hover:rotate-12 transition-transform duration-300`}>
                    <stat.icon className="text-white" size={28} />
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400 font-medium">{stat.detailLabel}</div>
                    <div className={`text-sm font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>
                      {stat.detail}
                    </div>
                  </div>
                </div>
                <div className="text-4xl font-black text-white mb-2 transform group-hover:scale-110 transition-transform origin-left">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-300 font-medium mb-1">{stat.label}</div>
                <div className="text-xs text-gray-500">{stat.subtitle}</div>
                
                {hoveredCard === idx && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-pulse"></div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Stats secondaires avec animations */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { icon: TrendingUp, label: 'Commandes en attente', value: stats.pendingOrders, color: 'orange', action: () => navigate('/orders') },
            { icon: Target, label: 'Taux de conversion', value: `${stats.totalProducts > 0 ? ((stats.activeProducts / stats.totalProducts) * 100).toFixed(1) : 0}%`, color: 'purple', action: () => {} },
            { icon: Zap, label: 'Taux de rupture', value: `${stats.totalProducts > 0 ? ((stats.outOfStock / stats.totalProducts) * 100).toFixed(1) : 0}%`, color: 'pink', action: () => {} }
          ].map((stat, idx) => (
            <div key={idx} onClick={stat.action} className="group relative overflow-hidden cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/10 rounded-2xl transform group-hover:scale-105 transition-transform duration-300"></div>
              <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-xl p-5 border border-white/20 hover:border-white/30 transition-all">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-400 mb-2 font-medium">{stat.label}</div>
                    <div className="text-3xl font-black text-white transform group-hover:scale-110 transition-transform origin-left">
                      {stat.value}
                    </div>
                  </div>
                  <div className={`w-12 h-12 bg-${stat.color}-500/20 rounded-xl flex items-center justify-center transform group-hover:rotate-12 transition-transform`}>
                    <stat.icon className={`text-${stat.color}-400`} size={24} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Section Produits avec animations */}
        <div className="bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden border border-white/20 mb-8 transform hover:shadow-3xl transition-all">
          <div className="relative overflow-hidden bg-gradient-to-r from-orange-500/20 via-pink-500/20 to-purple-500/20 p-6 border-b border-white/10">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-pink-500/10 animate-pulse"></div>
            <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-3xl font-black text-white mb-2 flex items-center space-x-3">
                  <Package className="text-orange-400" size={32} />
                  <span>Mes Produits</span>
                </h2>
                <p className="text-gray-300 text-sm flex items-center space-x-2 flex-wrap">
                  <span className="inline-flex items-center space-x-1 bg-green-500/20 px-2 py-1 rounded-lg">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    <span>{stats.activeProducts} actifs</span>
                  </span>
                  <span className="inline-flex items-center space-x-1 bg-yellow-500/20 px-2 py-1 rounded-lg">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                    <span>{stats.lowStock} stock faible</span>
                  </span>
                  <span className="inline-flex items-center space-x-1 bg-red-500/20 px-2 py-1 rounded-lg">
                    <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></span>
                    <span>{stats.outOfStock} rupture</span>
                  </span>
                </p>
              </div>
              <button 
                onClick={() => navigate('/vendor/add-product')}
                className="group relative overflow-hidden flex items-center justify-center space-x-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-2xl font-bold shadow-2xl transform hover:scale-105 transition-all"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <Plus size={24} className="relative z-10 group-hover:rotate-90 transition-transform" />
                <span className="relative z-10">Nouveau Produit</span>
              </button>
            </div>
          </div>

          {products.length === 0 ? (
            <div className="p-16 text-center">
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-2xl animate-pulse"></div>
                <div className="relative w-24 h-24 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-xl border border-white/20">
                  <Package size={48} className="text-orange-400" />
                </div>
              </div>
              <p className="text-2xl font-bold mb-3 text-white">Aucun produit</p>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">Commencez à développer votre boutique en ajoutant votre premier produit dès maintenant</p>
              <button 
                onClick={() => navigate('/vendor/add-product')}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-2xl font-bold shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
              >
                <Plus size={24} />
                <span>Ajouter un produit</span>
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5 backdrop-blur-xl">
                  <tr>
                    {['Produit', 'Prix', 'Stock', 'Catégorie', 'Actions'].map((header, idx) => (
                      <th key={idx} className={`px-6 py-5 text-left text-sm font-bold text-gray-300 uppercase tracking-wider ${idx === 3 ? 'hidden md:table-cell' : ''} ${idx === 4 ? 'text-right' : ''}`}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {products.map((product, idx) => {
                    const stock = Number(product.stock) || 0;
                    const stockStatus = stock === 0 ? 'out' : stock < 10 ? 'low' : 'good';
                    
                    return (
                      <tr key={product.id} className="group hover:bg-white/5 transition-all" style={{ animationDelay: `${idx * 50}ms` }}>
                        <td className="px-6 py-5">
                          <div className="flex items-center space-x-4">
                            <div className="relative">
                              <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl blur opacity-0 group-hover:opacity-50 transition"></div>
                              <img 
                                src={getImageUrl(product.image)} 
                                alt={product.name} 
                                className="relative w-16 h-16 rounded-2xl object-cover border-2 border-white/20 shadow-lg transform group-hover:scale-110 transition-transform" 
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/80x80?text=Image';
                                }}
                              />
                            </div>
                            <div className="min-w-0">
                              <div className="font-bold text-white truncate group-hover:text-orange-400 transition-colors">
                                {product.name}
                              </div>
                              <div className="text-sm text-gray-500">ID: {product.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="text-lg font-bold text-white">
                            {product.price?.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">FCFA</div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center space-x-2">
                            <span className={`px-4 py-2 rounded-xl text-sm font-bold border-2 backdrop-blur-xl transform group-hover:scale-110 transition-transform ${
                              stockStatus === 'out' 
                                ? 'bg-red-500/20 text-red-400 border-red-500/50 shadow-lg shadow-red-500/20' 
                                : stockStatus === 'low' 
                                  ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50 shadow-lg shadow-yellow-500/20'
                                  : 'bg-green-500/20 text-green-400 border-green-500/50 shadow-lg shadow-green-500/20'
                            }`}>
                              {stock}
                            </span>
                            {stockStatus !== 'good' && (
                              <AlertTriangle size={18} className={`${stockStatus === 'out' ? 'text-red-400' : 'text-yellow-400'} animate-pulse`} />
                            )}
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-6 py-5 text-gray-300 font-medium">
                          {product.category_name || '-'}
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center justify-end space-x-2">
                            {[
                              { icon: Eye, color: 'blue', action: () => navigate(`/product/${product.id}`), label: 'Voir' },
                              { icon: Edit, color: 'orange', action: () => navigate(`/vendor/edit-product/${product.id}`), label: 'Modifier' },
                              { icon: Trash2, color: 'red', action: () => handleDeleteProduct(product.id), label: 'Supprimer' }
                            ].map((btn, btnIdx) => (
                              <button 
                                key={btnIdx}
                                onClick={btn.action} 
                                className={`group/btn relative p-2.5 text-${btn.color}-400 hover:bg-${btn.color}-500/20 rounded-xl transition-all border border-transparent hover:border-${btn.color}-500/50 transform hover:scale-110`}
                                title={btn.label}
                              >
                                <div className={`absolute inset-0 bg-${btn.color}-500/10 rounded-xl opacity-0 group-hover/btn:opacity-100 transition-opacity`}></div>
                                <btn.icon size={18} className="relative z-10" />
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        
      </main>
    </div>
  );
}

// {/* Commandes récentes - Harmonisé avec BuyerDashboard */}
//         <div className="group/section relative">
//           <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/15 transition-all">
//             <div className="flex items-center justify-between mb-6">
//               <h2 className="text-2xl sm:text-3xl font-black text-white flex items-center space-x-3">
//                 <TrendingUp className="text-orange-400" size={32} />
//                 <span>Commandes récentes</span>
//               </h2>
//               <button 
//                 onClick={() => navigate('/orders')}
//                 className="group/btn relative overflow-hidden flex items-center space-x-2 bg-gradient-to-r from-orange-500/20 to-pink-500/20 px-4 sm:px-6 py-2.5 rounded-xl hover:from-orange-500/30 hover:to-pink-500/30 transition-all border border-orange-500/30 hover:border-orange-500/50"
//               >
//                 <span className="text-orange-300 font-medium text-sm">Voir tout</span>
//                 <Eye size={16} className="text-orange-400 group-hover/btn:translate-x-1 transition-transform" />
//               </button>
//             </div>
            
//             {orders.length === 0 ? (
//               <div className="text-center py-16">
//                 <div className="relative inline-block mb-6">
//                   <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-2xl animate-pulse"></div>
//                   <div className="relative w-24 h-24 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-xl border border-white/20">
//                     <ShoppingBag size={48} className="text-orange-400" />
//                   </div>
//                 </div>
//                 <p className="text-2xl font-bold mb-3 text-white">Aucune commande</p>
//                 <p className="text-gray-400 mb-8 max-w-md mx-auto">Vos commandes en attente et terminées apparaîtront ici.</p>
//                 <button 
//                   onClick={() => navigate('/orders')} 
//                   className="group/shop relative overflow-hidden inline-flex items-center space-x-2"
//                 >
//                   <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-pink-600 opacity-0 group-hover/shop:opacity-100 transition-opacity rounded-2xl"></div>
//                   <div className="relative bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-2xl font-bold shadow-2xl transform group-hover/shop:scale-105 transition-all flex items-center space-x-2">
//                     <Eye size={20} />
//                     <span>Voir toutes les commandes</span>
//                   </div>
//                 </button>
//               </div>
//             ) : (
//               <div className="space-y-6">
//                 {orders.slice(0, 3).map((order, idx) => {
//                   const statusConfig = getStatusConfig(order.status);
//                   const StatusIcon = statusConfig.icon;
                  
//                   return (
//                     <div 
//                       key={order.id}
//                       className="group relative"
//                       onClick={() => navigate('/orders')}
//                       style={{ animationDelay: `${idx * 100}ms` }}
//                     >
//                       <div className={`absolute -inset-0.5 bg-gradient-to-r ${statusConfig.gradient} rounded-3xl blur opacity-0 group-hover:opacity-5 transition duration-500`}></div>
//                       <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden border border-white/20 hover:bg-white/15 transition-all transform group-hover:scale-[1.01] cursor-pointer">
//                         {/* Header commande avec status */}
//                         <div className={`relative overflow-hidden bg-gradient-to-r ${statusConfig.gradient} bg-opacity-20 p-6 border-b border-white/10`}>
//                           <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent"></div>
//                           <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
//                             <div className="flex items-start space-x-4">
//                               <div className="relative">
//                                 <div className={`absolute inset-0 bg-gradient-to-br ${statusConfig.gradient} rounded-2xl blur opacity-75 animate-pulse`}></div>
//                                 <div className={`relative w-14 h-14 bg-gradient-to-br ${statusConfig.gradient} rounded-2xl flex items-center justify-center shadow-xl`}>
//                                   <StatusIcon className="text-white" size={28} />
//                                 </div>
//                               </div>
//                               <div>
//                                 <div className="flex items-center space-x-3 mb-2">
//                                   <h3 className="text-2xl font-black text-white">
//                                     Commande #{order.id}
//                                   </h3>
//                                   <span className={`px-4 py-2 rounded-xl text-sm font-bold border-2 backdrop-blur-xl shadow-lg ${statusConfig.bgClass} flex items-center space-x-2`}>
//                                     <span className="w-2 h-2 rounded-full bg-current animate-pulse"></span>
//                                     <span>{statusConfig.label}</span>
//                                   </span>
//                                 </div>
//                                 <p className="text-sm text-gray-300 mb-2 hidden sm:block">{statusConfig.description}</p>
//                                 <div className="flex items-center space-x-4 text-sm text-gray-400">
//                                   <div className="flex items-center space-x-1">
//                                     <Calendar size={16} />
//                                     <span>{new Date(order.created_at).toLocaleDateString('fr-FR', { 
//                                       day: 'numeric', 
//                                       month: 'long', 
//                                       year: 'numeric' 
//                                     })}</span>
//                                   </div>
//                                   <div className="flex items-center space-x-1">
//                                     <Package size={16} />
//                                     <span>{order.items?.length || 0} article(s)</span>
//                                   </div>
//                                   <div className="text-sm text-gray-400">Client: {order.buyer_name || 'N/A'}</div>

//                                 </div>
//                               </div>
//                             </div>
//                             <div className="text-right">
//                               <div className="text-sm text-gray-400 mb-1 font-medium">Montant Total</div>
//                               <div className="text-4xl font-black text-white group-hover:text-orange-400 transition-colors">
//                                 {parseFloat(order.total_amount).toLocaleString()}
//                               </div>
//                               <div className="text-sm text-gray-500">FCFA</div>
//                             </div>
//                           </div>
//                         </div>

//                         {/* Articles */}
//                         <div className="p-6">
//                           <h4 className="text-sm font-black text-gray-400 mb-4 uppercase tracking-wider flex items-center space-x-2">
//                             <Package size={16} className="text-orange-400" />
//                             <span>Articles commandés (les 2 premiers)</span>
//                           </h4>
//                           <div className="space-y-3">
//                             {order.items?.slice(0, 2).map((item, itemIdx) => (
//                               <div 
//                                 key={item.id || itemIdx}
//                                 className="group/item relative overflow-hidden"
//                               >
//                                 <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-500/5 to-orange-500/0 opacity-0 group-hover/item:opacity-100 transition-opacity rounded-xl"></div>
//                                 <div className="relative flex items-center space-x-4 bg-white/5 rounded-xl p-4 border border-white/10 group-hover/item:border-orange-500/30 transition-all">
//                                   <div className="relative">
//                                     <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl blur opacity-0 group-hover/item:opacity-50 transition"></div>
//                                     <img 
//                                       src={getImageUrl(item.product_image)}
//                                       alt={item.product_name}
//                                       className="relative w-20 h-20 rounded-2xl object-cover border-2 border-white/20 shadow-lg transform group-hover/item:scale-110 transition-transform"
//                                       onError={(e) => {
//                                         e.target.src = 'https://via.placeholder.com/80x80?text=Produit';
//                                       }}
//                                     />
//                                   </div>
//                                   <div className="flex-1 min-w-0">
//                                     <div className="font-bold text-white truncate text-lg group-hover/item:text-orange-400 transition-colors">
//                                       {item.product_name}
//                                     </div>
//                                     <div className="text-sm text-gray-400 flex items-center space-x-2 mt-1">
//                                       <span className="font-medium">Quantité: {item.quantity}</span>
//                                       <span className="text-gray-600">×</span>
//                                       <span>{parseFloat(item.price).toLocaleString()} FCFA</span>
//                                     </div>
//                                   </div>
//                                   <div className="text-right">
//                                     <div className="text-2xl font-black text-white group-hover/item:text-orange-400 transition-colors">
//                                       {parseFloat(item.subtotal).toLocaleString()}
//                                     </div>
//                                     <div className="text-xs text-gray-500">FCFA</div>
//                                   </div>
//                                 </div>
//                               </div>
//                             ))}
//                             {order.items?.length > 2 && (
//                                 <div className="text-center py-2 bg-white/5 rounded-xl border border-white/10">
//                                   <span className="text-sm text-orange-400 font-medium">
//                                     +{order.items.length - 2} autre(s) produit(s)
//                                   </span>
//                                 </div>
//                               )}
//                           </div>
//                         </div>
                        
//                         {/* Actions rapides */}
//                         {order.status === 'pending' && (
//                             <div className="p-6 pt-0 flex flex-wrap gap-3">
//                                 <button 
//                                     onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}/validate`); }}
//                                     className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 rounded-xl hover:from-green-500/30 hover:to-emerald-500/30 transition-all border border-green-500/30 hover:border-green-500/50 font-medium text-sm"
//                                 >
//                                     <CheckCircle2 size={18} />
//                                     <span>Valider la commande</span>
//                                 </button>
//                                 <button 
//                                     onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}/contact`); }}
//                                     className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-400 rounded-xl hover:from-blue-500/30 hover:to-cyan-500/30 transition-all border border-blue-500/30 hover:border-blue-500/50 font-medium text-sm"
//                                 >
//                                     <MessageCircle size={18} />
//                                     <span>Contacter le client</span>
//                                 </button>
//                             </div>
//                         )}
//                         {order.status === 'delivered' && (
//                             <div className="p-6 pt-0 flex flex-wrap gap-3">
//                                 <button 
//                                     onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}/view-feedback`); }}
//                                     className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 rounded-xl hover:from-yellow-500/30 hover:to-orange-500/30 transition-all border border-yellow-500/30 hover:border-yellow-500/50 font-medium text-sm"
//                                 >
//                                     <Star size={18} />
//                                     <span>Voir l'avis</span>
//                                 </button>
//                                 <button 
//                                     onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}/invoice`); }}
//                                     className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400 rounded-xl hover:from-purple-500/30 hover:to-pink-500/30 transition-all border border-purple-500/30 hover:border-purple-500/50 font-medium text-sm"
//                                 >
//                                     <DollarSign size={18} />
//                                     <span>Facture</span>
//                                 </button>
//                             </div>
//                         )}
                        
//                       </div>
//                     </div>
//                   );
//                 })}
//               </div>
//             )}
//           </div>
//         </div>